import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

type Props = {
  onRecordingComplete: (blob: Blob, mimeType: string) => void;
  disabled?: boolean;
  /** Si el dispositivo no soporta grabación, se muestra esto (p. ej. botón enviar). */
  unsupportedFallback?: ReactNode;
  /** Aviso cuando la nota se descarta por ser demasiado corta. */
  onTooShort?: () => void;
};

type RecorderState = 'idle' | 'recording';

const PREFERRED_MIME_TYPES = [
  // Preferidos por WhatsApp (Cloud / voice). WebM lo suele rechazar Meta.
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/mp4',
  'audio/webm;codecs=opus',
  'audio/webm',
];

const MIN_RECORDING_MS = 600;

function getSupportedMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  for (const mime of PREFERRED_MIME_TYPES) {
    if (MediaRecorder.isTypeSupported(mime)) return mime;
  }
  return null;
}

function isTouchDevice(): boolean {
  if (typeof window === 'undefined') return false;
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

function formatElapsed(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="22" />
    </svg>
  );
}

function StopIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
      <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
  );
}

export function VoiceNoteRecorder({
  onRecordingComplete,
  disabled,
  unsupportedFallback = null,
  onTooShort,
}: Props) {
  const [state, setState] = useState<RecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [supported, setSupported] = useState<boolean | null>(null);
  const [isMobile] = useState(() => isTouchDevice());

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mimeTypeRef = useRef('');
  const recordingRef = useRef(false);
  const startingRef = useRef(false);
  const stopQueuedRef = useRef(false);
  const startedAtRef = useRef(0);
  const onTooShortRef = useRef(onTooShort);
  onTooShortRef.current = onTooShort;

  useEffect(() => {
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setSupported(false);
      return;
    }
    const mime = getSupportedMimeType();
    if (!mime) {
      setSupported(false);
      return;
    }
    mimeTypeRef.current = mime;
    setSupported(true);
  }, []);

  const cleanupStream = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    if (disabled || recordingRef.current || startingRef.current || supported === false) return;
    startingRef.current = true;
    stopQueuedRef.current = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // pointerUp llegó mientras pedíamos el micrófono: abortar sin grabar.
      if (stopQueuedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        startingRef.current = false;
        stopQueuedRef.current = false;
        onTooShortRef.current?.();
        return;
      }
      streamRef.current = stream;
      const mime = mimeTypeRef.current || getSupportedMimeType();
      if (!mime) {
        stream.getTracks().forEach((t) => t.stop());
        setSupported(false);
        startingRef.current = false;
        return;
      }
      mimeTypeRef.current = mime;
      const recorder = new MediaRecorder(stream, { mimeType: mime });
      mediaRecorderRef.current = recorder;
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        const elapsedMs = Date.now() - startedAtRef.current;
        cleanupStream();
        recordingRef.current = false;
        startingRef.current = false;
        stopQueuedRef.current = false;
        setState('idle');
        setElapsed(0);
        // Evita notas vacías por tap accidental (sobre todo hold-to-record en iOS).
        if (blob.size > 0 && elapsedMs >= MIN_RECORDING_MS) {
          onRecordingComplete(blob, mime);
        } else {
          onTooShortRef.current?.();
        }
      };
      recorder.start();
      recordingRef.current = true;
      startingRef.current = false;
      startedAtRef.current = Date.now();
      setState('recording');
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((n) => n + 1), 1000);
      // pointerUp llegó durante el arranque: parar apenas quede en recording.
      if (stopQueuedRef.current && recorder.state === 'recording') {
        recorder.stop();
      }
    } catch {
      setSupported(false);
      startingRef.current = false;
      stopQueuedRef.current = false;
      cleanupStream();
    }
  }, [cleanupStream, disabled, onRecordingComplete, supported]);

  const stopRecording = useCallback(() => {
    if (startingRef.current) {
      stopQueuedRef.current = true;
      return;
    }
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  useEffect(() => {
    return () => {
      cleanupStream();
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, [cleanupStream]);

  if (supported === false) return <>{unsupportedFallback}</>;
  if (supported === null) return <>{unsupportedFallback}</>;

  if (state === 'recording') {
    return (
      <div className="voice-note-recorder voice-note-recorder--active">
        <span className="voice-note-recorder__pulse" aria-hidden />
        <span className="voice-note-recorder__timer">{formatElapsed(elapsed)}</span>
        <button
          type="button"
          className="voice-note-recorder__stop"
          aria-label="Enviar nota de voz"
          onClick={stopRecording}
          onPointerUp={isMobile ? stopRecording : undefined}
        >
          <StopIcon />
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="voice-note-recorder__mic"
      title={isMobile ? 'Mantené pulsado para grabar' : 'Grabar nota de voz'}
      aria-label="Grabar nota de voz"
      disabled={disabled || supported !== true}
      onClick={isMobile ? undefined : () => void startRecording()}
      onPointerDown={
        isMobile ?
          (e) => {
            e.preventDefault();
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            void startRecording();
          }
        : undefined
      }
      onPointerUp={
        isMobile ?
          (e) => {
            e.preventDefault();
            (e.target as HTMLElement).releasePointerCapture(e.pointerId);
            stopRecording();
          }
        : undefined
      }
      onPointerCancel={isMobile ? stopRecording : undefined}
    >
      <MicIcon />
    </button>
  );
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}
